import { el } from "../dom.ts";
import { t } from "../i18n.ts";
import { moduleIconSvg } from "../icons.ts";
import { formatModuleCount } from "../module-units.ts";
import { moduleById } from "../../modules/registry.ts";
import type { ViewerSource, HtmlItem, FileItem } from "./source.ts";
import type { ModuleIndexData, IndexRow } from "../../modules/shared/module-index.ts";
import type { ModuleId } from "../../auth/config.ts";

/**
 * In-app viewer: a full-screen master–detail browser over one module's
 * exported data (see source.ts for where the data comes from).
 *
 * This replaces opening generated HTML files in a new tab. Everything is
 * rendered inside the live app — item content injected into an element,
 * images loaded as blob sub-resources, files offered as downloads — so there
 * is never a top-level navigation to a blob: URL, which is the thing
 * Chromium blocks with "about:blank#blocked". The counts and rows come
 * straight from the module's index.json, so what the viewer shows always
 * matches the standalone index page.
 */

const PREVIEWABLE_IMAGE = /\.(png|jpe?g|gif|webp|svg|bmp)$/i;
const PREVIEWABLE_PDF = /\.pdf$/i;

function isImage(name: string, type: string): boolean {
    return type.startsWith("image/") || PREVIEWABLE_IMAGE.test(name);
}

function isPdf(name: string, type: string): boolean {
    return type === "application/pdf" || PREVIEWABLE_PDF.test(name);
}

function rowText(row: IndexRow): string {
    return Object.values(row.cells).join(" ").toLowerCase();
}

export interface Viewer {
    readonly root: HTMLElement;
    open(moduleId: ModuleId): void;
}

export function createViewer(source: ViewerSource): Viewer {
    /* ---- structure ------------------------------------------------------ */

    const iconEl = el("span", { class: "viewer-icon" });
    const titleEl = el("h2", { class: "viewer-title" });
    const subEl = el("span", { class: "viewer-sub" });
    const closeButton = el("button", { class: "viewer-close", type: "button", "aria-label": t("common.dismiss") }, ["✕"]);

    const head = el("header", { class: "viewer-head" }, [
        iconEl,
        el("div", { class: "viewer-titles" }, [titleEl, subEl]),
        closeButton
    ]);

    const searchInput = el("input", {
        type: "search",
        class: "viewer-search",
        autocomplete: "off"
    }) as HTMLInputElement;
    const itemsList = el("ul", { class: "viewer-items" });
    const listPane = el("div", { class: "viewer-list" }, [searchInput, itemsList]);

    const detailPane = el("div", { class: "viewer-detail" });

    const panel = el("div", { class: "viewer-panel", role: "dialog", "aria-modal": "true" }, [
        head,
        el("div", { class: "viewer-body" }, [listPane, detailPane])
    ]);
    const overlay = el("div", { class: "viewer-overlay" }, [panel]);
    overlay.hidden = true;

    /* ---- state ---------------------------------------------------------- */

    let current: ModuleIndexData | null = null;
    let currentModule: ModuleId | null = null;
    let selectedRow: HTMLElement | null = null;
    let disposeDetail: (() => void) | null = null;

    const clearDetail = () => {
        disposeDetail?.();
        disposeDetail = null;
        detailPane.replaceChildren();
    };

    const setDetailMessage = (message: string) => {
        clearDetail();
        detailPane.append(el("div", { class: "viewer-empty" }, [message]));
    };

    /* ---- detail renderers ---------------------------------------------- */

    const showDataDetail = (row: IndexRow) => {
        clearDetail();
        if (!current) {
            return;
        }
        const dl = el("dl", { class: "viewer-fields" });
        for (const column of current.columns) {
            const value = row.cells[column.key];
            if (!value) {
                continue;
            }
            dl.append(
                el("dt", {}, [column.label]),
                el("dd", {}, [value])
            );
        }
        detailPane.append(el("div", { class: "viewer-doc viewer-card" }, [dl]));
    };

    const showHtmlDetail = async (moduleId: ModuleId, href: string) => {
        setDetailMessage(t("viewer.loading"));
        let item: HtmlItem;
        try {
            item = await source.loadHtml(moduleId, href);
        } catch {
            setDetailMessage(t("viewer.itemError"));
            return;
        }
        clearDetail();
        disposeDetail = item.revoke;

        const doc = el("div", { class: "viewer-doc" });
        doc.innerHTML = item.bodyHtml;
        // Absolute links open in a new tab; anything else is inert (relative
        // links were already stripped in inlineHtmlAssets).
        for (const anchor of doc.querySelectorAll("a")) {
            const href2 = anchor.getAttribute("href");
            if (href2 && /^https?:/i.test(href2)) {
                anchor.setAttribute("target", "_blank");
                anchor.setAttribute("rel", "noopener noreferrer");
            } else {
                anchor.removeAttribute("href");
            }
        }
        detailPane.append(doc);
    };

    const showFileDetail = async (moduleId: ModuleId, href: string, row: IndexRow) => {
        setDetailMessage(t("viewer.loading"));
        let item: FileItem;
        try {
            item = await source.loadFile(moduleId, href);
        } catch {
            setDetailMessage(t("viewer.itemError"));
            return;
        }
        clearDetail();
        disposeDetail = item.revoke;

        const card = el("div", { class: "viewer-doc viewer-card" });

        // Metadata already in the row (subject/from/date for mail, path/size
        // for OneDrive) — shown above the preview so an .eml that the browser
        // can't render still tells the user what it is.
        if (current) {
            const dl = el("dl", { class: "viewer-fields" });
            for (const column of current.columns) {
                const value = row.cells[column.key];
                if (!value) {
                    continue;
                }
                dl.append(el("dt", {}, [column.label]), el("dd", {}, [value]));
            }
            card.append(dl);
        }

        if (isImage(item.name, item.type)) {
            card.append(el("img", { class: "viewer-preview", src: item.url, alt: item.name }));
        } else if (isPdf(item.name, item.type)) {
            const frame = el("iframe", { class: "viewer-preview-frame", src: item.url }) as HTMLIFrameElement;
            frame.title = item.name;
            card.append(frame);
        }

        const download = el("a", {
            class: "viewer-download",
            href: item.url,
            download: item.name
        }, [t("viewer.download", { name: item.name })]);
        card.append(el("div", { class: "viewer-actions" }, [download]));

        detailPane.append(card);
    };

    const showDetail = (row: IndexRow) => {
        if (!current || !currentModule) {
            return;
        }
        if (current.itemKind === "data" || !row.href) {
            showDataDetail(row);
        } else if (current.itemKind === "html") {
            void showHtmlDetail(currentModule, row.href);
        } else {
            void showFileDetail(currentModule, row.href, row);
        }
    };

    /* ---- list ----------------------------------------------------------- */

    const renderList = () => {
        itemsList.replaceChildren();
        selectedRow = null;
        if (!current) {
            return;
        }
        const query = searchInput.value.trim().toLowerCase();
        const terms = query ? query.split(/\s+/) : [];
        const [primaryCol, secondaryCol] = current.columns;

        let shown = 0;
        for (const row of current.rows) {
            if (terms.length > 0) {
                const hay = rowText(row);
                if (!terms.every((term) => hay.includes(term))) {
                    continue;
                }
            }
            shown += 1;

            const primary = primaryCol ? row.cells[primaryCol.key] ?? "" : "";
            const secondary = secondaryCol ? row.cells[secondaryCol.key] ?? "" : "";

            const li = el("li", { class: "viewer-item", tabindex: "0", role: "button" }, [
                el("span", { class: "viewer-item-primary" }, [primary || "—"]),
                ...(secondary ? [el("span", { class: "viewer-item-secondary" }, [secondary])] : [])
            ]);

            const select = () => {
                if (selectedRow) {
                    selectedRow.classList.remove("is-selected");
                }
                li.classList.add("is-selected");
                selectedRow = li;
                showDetail(row);
            };
            li.addEventListener("click", select);
            li.addEventListener("keydown", (event) => {
                if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    select();
                }
            });
            itemsList.append(li);
        }

        if (shown === 0) {
            itemsList.append(el("li", { class: "viewer-item-empty" }, [t("viewer.noMatch")]));
        }
    };

    searchInput.addEventListener("input", renderList);

    /* ---- open / close --------------------------------------------------- */

    const close = () => {
        clearDetail();
        overlay.hidden = true;
        document.body.classList.remove("viewer-open");
        current = null;
        currentModule = null;
    };

    const open = async (moduleId: ModuleId) => {
        currentModule = moduleId;
        const definition = moduleById(moduleId);

        iconEl.innerHTML = moduleIconSvg(moduleId);
        titleEl.textContent = definition ? t(definition.labelKey) : moduleId;
        subEl.textContent = "";
        searchInput.value = "";
        searchInput.placeholder = t("viewer.search");
        itemsList.replaceChildren();
        setDetailMessage(t("viewer.selectPrompt"));

        overlay.hidden = false;
        document.body.classList.add("viewer-open");

        let data: ModuleIndexData | null;
        try {
            data = await source.loadIndex(moduleId);
        } catch {
            data = null;
        }

        // A different module may have been opened while this was loading.
        if (currentModule !== moduleId) {
            return;
        }

        current = data;
        if (!data || data.rows.length === 0) {
            subEl.textContent = "";
            setDetailMessage(t("viewer.emptyModule"));
            return;
        }

        subEl.textContent = formatModuleCount(data.rows.length, moduleId);
        renderList();
        searchInput.focus();
    };

    closeButton.addEventListener("click", close);
    overlay.addEventListener("click", (event) => {
        if (event.target === overlay) {
            close();
        }
    });
    document.addEventListener("keydown", (event) => {
        if (event.key === "Escape" && !overlay.hidden) {
            close();
        }
    });

    return {
        root: overlay,
        open(moduleId) {
            void open(moduleId);
        }
    };
}
